import { getDeliveredPackages } from "api/routes";
import dayjs from "dayjs";
import { create } from "zustand";

// Último payload aplicado, en UN solo slot (solo hay una vista de rutas
// montada a la vez). Si el backend devuelve exactamente lo mismo que el poll
// anterior, saltamos el set(): sin identidad nueva no hay re-render ni
// re-adaptación aguas abajo. La comparación por string es exacta; el
// stringify (~176KB) cuesta pocos ms contra la cascada que evita.
// REPUBLISH_MS: aunque el payload no cambie, re-publicamos identidad pasado
// este plazo — hay derivaciones aguas abajo dependientes de "ahora" (la
// horaEstimada placeholder de pendientes) que no deben congelarse
// indefinidamente en horas tranquilas.
const REPUBLISH_MS = 180000;
let lastApplied = { key: null, json: null, at: 0 };
// Identidad POR DRIVER entre polls: cuando el payload cambia, casi siempre
// cambió UN repartidor de decenas. Reutilizar el objeto anterior de cada driver
// cuyo JSON no cambió permite que la cache de adaptación (useActividadData) y
// React.memo (RouteCard) se salten a todos los que no cambiaron — sin esto,
// cada poll con cambios re-adaptaba y re-renderizaba la lista completa porque
// el parse de Axios crea objetos nuevos para todos. Un solo slot, como lastApplied.
let driverCache = { key: null, byId: new Map() };

const useDeliveredStore = create((set) => ({
    delivered: {},       // { [YYYY-MM-DD]: delivererData[] }
    expandedDriverId: null, // ID del repartidor con el mapa expandido
    lastFetchedAt: null,
    // Timestamp del último fetch FALLIDO (null si el último fue exitoso). La UI
    // lo usa para avisar que está mostrando datos viejos — antes el error moría
    // en un console.error y la vista seguía diciendo "Actualizado HH:MM".
    fetchError: null,

    fetchDelivered: async (date, filters, timezone) => {
        try {
            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const deliveredPackages = await getDeliveredPackages(formattedDate, { ...filters, timezone });
            const key = `${formattedDate}|${filters?.logisticCompanyId || ""}|${filters?.companyId || ""}`;
            const json = JSON.stringify(deliveredPackages);
            if (
                lastApplied.key === key &&
                lastApplied.json === json &&
                Date.now() - lastApplied.at < REPUBLISH_MS
            ) {
                set((state) => (state.fetchError === null ? state : { fetchError: null }));
                return;
            }
            const prevById = driverCache.key === key ? driverCache.byId : new Map();
            const byId = new Map();
            const merged = deliveredPackages.map((d, i) => {
                const id = d._id ?? `i${i}`;
                const js = JSON.stringify(d);
                const hit = prevById.get(id);
                const obj = hit && hit.json === js ? hit.obj : d;
                byId.set(id, { json: js, obj });
                return obj;
            });
            driverCache = { key, byId };
            lastApplied = { key, json, at: Date.now() };
            set((state) => ({
                delivered: {
                    ...state.delivered,
                    [formattedDate]: merged,
                },
                lastFetchedAt: Date.now(),
                fetchError: null,
            }));
        } catch (error) {
            console.error("Error fetching delivered:", error);
            // Re-lanzar: el hook de polling distingue éxito/fallo (con esto su
            // rama catch —reintento a ritmo idle, sin bumpear lastUpdated— por
            // fin se ejecuta; antes este catch se tragaba todo y el polling
            // marcaba "Actualizado" aunque el fetch hubiera muerto). fetchError
            // NO se escribe acá: lo escribe el hook DESPUÉS de su guard de
            // generación — una cadena muerta (cambio de fecha con fetch en
            // vuelo) no debe pintar error sobre la cadena viva.
            throw error;
        }
    },

    setExpandedDriver: (driverId) =>
        set((state) => ({
            expandedDriverId: state.expandedDriverId === driverId ? null : driverId,
        })),
}));

export default useDeliveredStore;
